import React, { useRef } from 'react';

import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  Sortable,
  SortableItem,
  SortableItemHandle,
  arrayMove,
} from 'components/DesignSystems/Sortable/Sortable';
import {
  groupSectionsByCategory,
  sectionsHaveCategories,
  type SectionGroup,
} from 'components/Reports/sitrepSectionGrouping';
import { cn } from 'cn';

const CATEGORY_GROUP_CLASSES = 'tw:flex tw:flex-col tw:gap-0.5';
const SECTION_ITEM_CLASSES =
  'tw:flex tw:cursor-pointer tw:items-center tw:gap-1 tw:rounded-sm tw:border-0 tw:bg-transparent tw:py-1.5 tw:pr-2 tw:pl-6 tw:text-left tw:text-sm tw:text-(color:--text-weak) tw:transition-colors tw:duration-150 tw:hover:bg-(--background-light) tw:hover:text-(color:--text-white) tw:aria-selected:bg-(--background-light) tw:aria-selected:text-(color:--text-primary)';

type SitrepSectionDocumentViewProps = {
  sections: SitrepSectionType[];
  /**
   * Use activeSectionIndex from useSitrepSectionSelector for correct
   * highlighting.
   */
  activeSectionIndex: number | null;
  onSectionChange: (index: number) => void;
  /** Renders each section's body in the document pane. */
  renderSection: (section: SitrepSectionType, index: number) => React.ReactNode;
  /**
   * Persists a new category order by passing every section ID in the desired
   * final order (each category's sections move together as a block). Omit to
   * render the category tree as read-only (e.g. reviewers without authoring
   * permissions).
   */
  onReorderGroups?: (orderedSectionIds: string[]) => void;
  className?: string;
  idBase?: string;
};

const groupId = (group: SectionGroup, groupIndex: number): string =>
  group.category ?? `uncategorized-${groupIndex}`;

type CategoryGroupContentProps = {
  group: SectionGroup;
  selectedIndex: number;
  idBase?: string;
  onSectionChange: (index: number) => void;
  onSectionKeyDown: (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => void;
  setItemRef: (index: number, element: HTMLButtonElement | null) => void;
  showDragHandle?: boolean;
};

/**
 * Category label + its section list — shared by the draggable and static
 * renders.
 */
const CategoryGroupContent: React.FC<CategoryGroupContentProps> = ({
  group,
  selectedIndex,
  idBase,
  onSectionChange,
  onSectionKeyDown,
  setItemRef,
  showDragHandle = false,
}) => (
  <>
    {group.category && (
      <div className="tw:flex tw:items-center tw:px-2 tw:py-1.5 tw:text-[0.8125rem] tw:font-semibold tw:text-(color:--text-weak)">
        {showDragHandle && (
          <SortableItemHandle
            aria-label={`Reorder ${group.category}`}
            className="me-1"
          >
            <SharedIcon
              iconName="drag_indicator"
              size="sm"
              aria-hidden="true"
            />
          </SortableItemHandle>
        )}
        {group.category}
      </div>
    )}
    {group.sections.map(({ section, index }) => (
      <button
        key={section.id}
        type="button"
        id={idBase ? `${idBase}-tree-item-${index}` : undefined}
        role="treeitem"
        aria-selected={selectedIndex === index}
        aria-controls={idBase ? `${idBase}-panel` : undefined}
        tabIndex={selectedIndex === index ? 0 : -1}
        className={SECTION_ITEM_CLASSES}
        onClick={() => onSectionChange(index)}
        onKeyDown={(event) => onSectionKeyDown(event, index)}
        ref={(element) => setItemRef(index, element)}
      >
        <span className="d-flex align-items-center gap-1">
          {section.type === 'ai' && (
            <SharedIcon iconName="auto_awesome" size="sm" aria-hidden="true" />
          )}
          {section.name}
        </span>
      </button>
    ))}
  </>
);

/**
 * "Document" layout for SITREP sections: a directory-tree sidebar listing
 * categories with their nested sections, and every section rendered in one
 * scrollable document alongside it.
 */
const SitrepSectionDocumentView: React.FC<SitrepSectionDocumentViewProps> = ({
  sections,
  activeSectionIndex,
  onSectionChange,
  renderSection,
  onReorderGroups,
  className,
  idBase,
}) => {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  if (sections.length === 0) return null;

  const selectedIndex = activeSectionIndex ?? 0;
  const groups = groupSectionsByCategory(sections);
  const canReorderGroups =
    !!onReorderGroups && sectionsHaveCategories(sections) && groups.length > 1;
  const groupIds = groups.map((group, groupIndex) =>
    groupId(group, groupIndex)
  );

  const handleGroupReorder = (oldIndex: number, newIndex: number) => {
    if (!onReorderGroups) return;

    const reorderedGroups = arrayMove(groups, oldIndex, newIndex);
    const orderedSectionIds = reorderedGroups.flatMap((group) =>
      group.sections.map(({ section }) => section.id)
    );
    onReorderGroups(orderedSectionIds);
  };

  const selectSection = (index: number) => {
    onSectionChange(index);
    if (idBase) {
      document
        .getElementById(`${idBase}-document-section-${index}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const focusSection = (index: number) => {
    selectSection(index);
    itemRefs.current[index]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowDown':
        nextIndex = index === sections.length - 1 ? 0 : index + 1;
        break;
      case 'ArrowUp':
        nextIndex = index === 0 ? sections.length - 1 : index - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = sections.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    focusSection(nextIndex);
  };

  const setItemRef = (index: number, element: HTMLButtonElement | null) => {
    itemRefs.current[index] = element;
  };

  const tree = canReorderGroups ? (
    <Sortable items={groupIds} onReorder={handleGroupReorder}>
      {groups.map((group, groupIndex) => (
        <SortableItem
          key={groupId(group, groupIndex)}
          id={groupId(group, groupIndex)}
          className={cn(
            CATEGORY_GROUP_CLASSES,
            'tw:data-dragging:z-[1] tw:data-dragging:rounded-sm tw:data-dragging:bg-(--background-light) tw:data-dragging:opacity-60'
          )}
          role="group"
          aria-label={group.category ?? undefined}
        >
          <CategoryGroupContent
            group={group}
            selectedIndex={selectedIndex}
            idBase={idBase}
            onSectionChange={selectSection}
            onSectionKeyDown={handleKeyDown}
            setItemRef={setItemRef}
            showDragHandle
          />
        </SortableItem>
      ))}
    </Sortable>
  ) : (
    groups.map((group, groupIndex) => (
      <div
        key={groupId(group, groupIndex)}
        className={CATEGORY_GROUP_CLASSES}
        role="group"
        aria-label={group.category ?? undefined}
      >
        <CategoryGroupContent
          group={group}
          selectedIndex={selectedIndex}
          idBase={idBase}
          onSectionChange={selectSection}
          onSectionKeyDown={handleKeyDown}
          setItemRef={setItemRef}
        />
      </div>
    ))
  );

  return (
    <div className={cn('tw:flex tw:items-start tw:gap-4', className)}>
      <div
        className="tw:flex tw:max-w-[240px] tw:flex-[0_0_240px] tw:flex-col tw:gap-1 tw:rounded-md tw:border tw:border-(color:--neutral-600) tw:bg-(--background-dark) tw:p-2"
        role="tree"
        aria-label="SITREP sections"
      >
        {tree}
      </div>
      <div
        id={idBase ? `${idBase}-panel` : undefined}
        role="region"
        aria-label="SITREP document"
        className="tw:min-w-0 tw:flex-1 tw:rounded-md tw:border tw:border-(color:--neutral-600) tw:p-3"
      >
        {groups.map((group, groupIndex) => (
          <div key={groupId(group, groupIndex)}>
            {group.category && <h2>{group.category}</h2>}
            {group.sections.map(({ section, index }) => (
              <div
                key={section.id}
                id={idBase ? `${idBase}-document-section-${index}` : undefined}
                aria-current={selectedIndex === index ? 'true' : undefined}
              >
                {renderSection(section, index)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SitrepSectionDocumentView;
